import React, {
    useContext, useCallback, useEffect, useState
} from 'react';
import _ from 'lodash';
import { ModalNextProvider } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { commonMessages } from 'gw-capability-quoteandbind-common-react';
import metadata from './RiskAnalysisPage.metadata.json5';
import messages from './RiskAnalysisPage.messages';
import styles from './RiskAnalysisPage.module.scss';
import { PolicyChange, messages as commonMessage } from 'gw-capability-policychange-common-react';
import moment from 'moment';

function RiskAnalysisPage(props) {
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const { wizardData, updateWizardData } = props;
    const {submissionVM} = wizardData;
    const viewModelService = useContext(ViewModelServiceContext);
    const { EndorsementService } = useDependencies('EndorsementService');
    const { authHeader } = useAuthentication();
    const [isPageInitialized, setPageInitialized] = useState(false);
    const [checkNewDriverState, setCheckNewDriverState] = useState(false);
    const {
        registerInitialComponentValidation,
        isComponentValid,
        disregardFieldValidation,
        initialValidation,
        onValidate
    } = useValidation('VehiclePage');

    useEffect(() => {
        submissionVM.baseData.priorLossHistoryRecords.forEach((item) => {return item.isAutoValidationRequired.value = true} );
        submissionVM.baseData.hasUserVisitedRiskPage_EY.value = true;        
        setPageInitialized(true);
    }, []);

    useEffect(() => {
        setCheckNewDriverState(checkNewDriverAdded());
    }, [submissionVM,checkNewDriverState]);

    const derivedLossDriver = useCallback((index) => {
        const priorLossHistoryRecord = submissionVM.baseData.priorLossHistoryRecords.value[index];
        let  derivedLossDriverData= [];
        if(priorLossHistoryRecord !== undefined && priorLossHistoryRecord.isNewClaim){
            derivedLossDriverData = submissionVM.lobData.businessAuto.coverables.drivers.value.filter((driver) => {
                return driver.existingDriver_EY == false;
            }).map((c) => {
                return {
                    code: c.publicID,
                    name: c.person.displayName
                };
            })
        }else{
            derivedLossDriverData = submissionVM.lobData.businessAuto.coverables.drivers.value.filter((driver) => {
                return driver.existingDriver_EY == true;
            }).map((c) => {
                return {
                    code: c.publicID,
                    name: c.person.displayName
                };
            })
        } 
        return derivedLossDriverData;
    },[submissionVM]);

    const checkNewDriverAdded = useCallback(() => {
        const drivers = _.get(
            submissionVM,
            'lobData.businessAuto.coverables.drivers.value'
        );
        if(drivers.length > 0){
            if(drivers.every((driver)=>{ 
                return driver.existingDriver_EY == true;
            })){
                return false;
            }
        }
        return true;
    }, [submissionVM]);

    const addAdditionalInterest = useCallback(() => {
        const homeAdditionalInterests = _.get(
            submissionVM,
            'baseData.priorLossHistoryRecords'
        );
        homeAdditionalInterests.value = homeAdditionalInterests.value || [];
        const { _xCenter, _dtoName } = homeAdditionalInterests;
        const additionalVM = viewModelService.create({
            "productLine": submissionVM.baseData.productCode.value,
            "source": "portal_ey",
            "isNewClaim" : true
        }, _xCenter, _dtoName);
        homeAdditionalInterests.pushElement(additionalVM);
        _.set(submissionVM,'baseData.priorLossHistoryRecords',homeAdditionalInterests);
        wizardData.submissionVM = submissionVM;
        updateWizardData(wizardData);
    }, [submissionVM, updateWizardData, viewModelService]);

    const removeAdditionalInterest = useCallback(
        (evt) => {
            const additionInterestPath = evt.path;
            // removes `.children.index` and replace with `.value`
            // so we can get an array of Additional Interests
            const additionalInterestListPath = additionInterestPath.replace(
                /\.children\.\d+$/,
                '.value'
            );
            const additionalInterestToRemove = _.get(submissionVM, additionInterestPath);
            const additionalInterestList = _.get(submissionVM, additionalInterestListPath);

            ModalNextProvider.showConfirm({
                title: messages.removeRAClaimHeading,
                message: messages.removeRAClaimBody,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.additionalModalYes,
                cancelButtonText: messages.additionalModalNo
            }).then(
                (results) => {
                    if (results === 'cancel') {
                        return _.noop();
                    }
                    const newAdditionalInterestList = additionalInterestList.filter(
                        (additionalInterest, index) => {
                            // eslint-disable-next-line
                            const additionalInterestListPathWithoutValue = additionalInterestListPath.replace(
                                '.value',
                                ''
                            );
                            const currentAdditionalInterestPath = `${additionalInterestListPathWithoutValue}.children.${index}`;
                            // eslint-disable-next-line
                            const isCorrectPath = currentAdditionalInterestPath === additionInterestPath;

                            return !(
                                isCorrectPath
                                && _.isEqual(additionalInterest, additionalInterestToRemove.value)
                            );
                        }
                    );

                    _.set(submissionVM, additionalInterestListPath, newAdditionalInterestList);
                    wizardData.submissionVM = submissionVM;
                    updateWizardData(wizardData);
                    return true;
                }, _.noop
            );
        },
        [submissionVM, updateWizardData]
    );

    const additionalInterest = _.get(
        submissionVM,
        'baseData.priorLossHistoryRecords.value'
    );
    
    const overrideTitleProps = useCallback(() => {
        if (additionalInterest !== undefined) {
            const overrideProps = additionalInterest.map((value, index) => {
                const effectiveDate = moment(`${submissionVM.baseData.periodStartDate.year.value}-${submissionVM.baseData.periodStartDate.month.value +1}-${submissionVM.baseData.periodStartDate.day.value}`);
                let elmDate;
                if (value.lossDate) {
                    elmDate = moment(`${value.lossDate.year}-${value.lossDate.month +1}-${value.lossDate.day}`);
                }
                let isClaimNotOver6Years = value.isNewClaim ? true : effectiveDate.diff(elmDate, 'days') <= 2190;
                if (isClaimNotOver6Years) {
                    _.set(submissionVM, `baseData.priorLossHistoryRecords.children.${index}.isAutoValidationRequired`, true);
                }
                else {
                    _.set(submissionVM, `baseData.priorLossHistoryRecords.children.${index}.isAutoValidationRequired`, false);
                }
                if (value.atFaultPercent > 25) {
                    _.set(submissionVM, `baseData.priorLossHistoryRecords.children.${index}.chargeable`, value.atFaultPercent > 25);
                }
                return {
                // [`baAdditionalInterest${index}`]: {
                //     visible: index === 0
                // },
                    [`baAdditionalInterestCount${index}`]: {
                    // visible: index > 0,
                        content: translator(messages.claimCountTitle, {
                            count: index + 1
                        })
                    },
                    [`lossDriver${index}`]: {
                        //availableValues: derivedLossDriver(index)
                        availableValues : submissionVM.lobData.businessAuto.coverables.drivers.value.map((c) => {
                            return {
                                code: c.publicID,
                                name: c.person.displayName
                            };
                        })
                    },
                    [`lossVehicle${index}`]: {
                        availableValues: submissionVM.lobData.businessAuto.coverables.vehicles.value.map((c) => {
                            c['id'] = c.publicID;
                            return c
                        })
                    },
                    [`lossDate${index}`]: {
                        readOnly: !value.isNewClaim
                    },
                    [`lossAmount${index}`]: {
                        readOnly: !value.isNewClaim
                    },
                    [`atFaultPercent${index}`]: {
                        readOnly: !value.isNewClaim
                    },
                    [`hasLiabilty${index}`]: {
                        readOnly: !value.isNewClaim
                    },
                    [`hasAb${index}`]: {
                        readOnly: !value.isNewClaim
                    },
                    [`hasComp${index}`]: {
                        readOnly: !value.isNewClaim
                    },
                    [`hasApColl${index}`]: {
                        readOnly: !value.isNewClaim
                    },
                    [`hasDcpd${index}`]: {
                        readOnly: !value.isNewClaim
                    },
                    [`hasSp${index}`]: {
                        readOnly: !value.isNewClaim
                    },
                    [`additionalInterestIterableContainer${index}`]: {
                        visible: isClaimNotOver6Years
                    },
                    [`chargeableReadOnly${index}`]: {
                        visible: value.atFaultPercent > 25,
                        value: true
                    },
                    [`chargeable${index}`]: {
                        visible: !(value.atFaultPercent > 25)
                    }
                // [`additionalInterestIterableContainer${index}`] :{
                //     visible : true
                // } 
                };
            });

            return Object.assign({}, ...overrideProps);
        } else {return null}
    }, [additionalInterest, translator]);

    const validateCoverages = () => {
        var isCoveragesValid = true
        submissionVM.baseData.priorLossHistoryRecords.children.forEach((riskUnit) => {
            if(!( _.get(riskUnit, 'hasLiabilty.value') || _.get(riskUnit, 'hasAb.value') || _.get(riskUnit, 'hasComp.value') || _.get(riskUnit, 'hasApColl.value') || _.get(riskUnit, 'hasDcpd.value') || _.get(riskUnit, 'hasSp.value'))){
                isCoveragesValid = false;
            }            
        })
        return isCoveragesValid;
    }

    const onNext = useCallback(async () => {
        if(!validateCoverages()){
            try{
                await ModalNextProvider.showAlert({
                    title: commonMessages.error,
                    message: translator(messages.selectOneCoverage),
                    status: 'error',
                    icon: 'mi-error-outline'
                })
            } catch(err){

            }
            
            return false;
        }
        if (!submissionVM.value.validate()) {
            updateWizardData(wizardData);
            return false;
        }
        submissionVM.value.beforeSave();
        const policyChangeResponse = await EndorsementService.saveEndorsement(
            [submissionVM.value],
            authHeader
        );
        submissionVM.value = new PolicyChange(policyChangeResponse);
        return wizardData;
    }, [EndorsementService, authHeader, submissionVM, translator, messages]);

    const overrideProps = {
        '@field': {
            // apply to all fields
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showOptional: false,
            showRequired: true
        },
        addclaimDetailsContainer : {
            visible : checkNewDriverState
        },
        contentEmptyContainer:{
            visible: additionalInterest !== undefined ? additionalInterest.length == 0 : true
        },
        ...overrideTitleProps()
    };

    const resolvers = {
        resolveCallbackMap: {
            onAddAdditionalInterest: addAdditionalInterest,
            onRemoveAdditionalInterest: removeAdditionalInterest
        }
    };

    const updateSubmission = (_submissionVM) => {
        const newSubmissionVMClone = viewModelService.clone(_submissionVM);
        wizardData.submissionVM = newSubmissionVMClone;  
        updateWizardData(wizardData);
    }

    
    const handlePageValidation = useCallback(() => {
        const ratings = _.get(submissionVM.value, 'lobData.homeowners.coverables.rating');
        const quoteData = _.get(submissionVM.value, 'quoteData.offeredQuotes');
        const periodStatus = _.get(submissionVM.value, 'baseData.periodStatus');
        return (
            _.some(quoteData, (quote) => quote.status === 'Quoted')
            && periodStatus === 'Quoted'
            && ratings
        );
    }, [submissionVM]);

    useEffect(() => {
        registerInitialComponentValidation(handlePageValidation);
    }, [handlePageValidation, registerInitialComponentValidation]);

    if (!isPageInitialized) {
        return null;
    }

    return (
        <WizardPage onNext={onNext} disableNext={!isComponentValid} skipWhen={initialValidation}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateSubmission}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
}

RiskAnalysisPage.propTypes = wizardProps;
export default RiskAnalysisPage;
